import { forwardRef, useEffect, useId, useMemo, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { useCities } from '../api/use-marketplace';
import { detectCountryDomain } from '../lib/country-domain';
import { cn } from '../ui';

/**
 * Free-text city input with a dropdown of cities we have salons in. The
 * suggestions are progressive enhancement, not a hard select: typing filters
 * the list, picking a suggestion fills the input and submits.
 *
 * Styles (design-spec §3 "Inputs"):
 * - `segment` — hero search box segment: tiny uppercase label (WHERE) above a
 *   borderless input, no icon.
 * - `compact` — results-header group: pin icon + borderless input.
 * - `field`   — mobile hero: 46px bordered field with a pin icon.
 */
export type CityComboboxStyle = 'segment' | 'compact' | 'field';

export interface CityComboboxProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit?: (v: string) => void;
  placeholder?: string;
  variant?: CityComboboxStyle;
  /** Visible label (segment) / accessible name (other styles). */
  label: string;
  className?: string;
}

export const CityCombobox = forwardRef<HTMLInputElement, CityComboboxProps>(function CityCombobox(
  { value, onChange, onSubmit, placeholder, variant: style = 'compact', label, className },
  forwardedRef,
) {
  // On a country domain, scope suggestions to that country (results are
  // country-filtered too). null on salunas.com/localhost → all.
  const cities = useCities(detectCountryDomain().code);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const listId = `${id}-list`;

  const filtered = useMemo(() => {
    if (!cities.data) return [];
    const q = value.trim().toLowerCase();
    if (!q) return cities.data.slice(0, 8);
    const prefix = cities.data.filter((c) => c.name.toLowerCase().startsWith(q));
    const substr = cities.data.filter(
      (c) => !c.name.toLowerCase().startsWith(q) && c.name.toLowerCase().includes(q),
    );
    return [...prefix, ...substr].slice(0, 8);
  }, [cities.data, value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const commit = (v: string) => {
    onChange(v);
    onSubmit?.(v);
    setOpen(false);
  };

  const showList = open && filtered.length > 0;

  const input = (
    <input
      ref={(el) => {
        inputRef.current = el;
        if (typeof forwardedRef === 'function') forwardedRef(el);
        else if (forwardedRef) forwardedRef.current = el;
      }}
      id={id}
      type="text"
      role="combobox"
      aria-expanded={showList}
      aria-controls={showList ? listId : undefined}
      aria-autocomplete="list"
      aria-activedescendant={showList && highlight >= 0 ? `${listId}-${highlight}` : undefined}
      aria-label={style === 'segment' ? undefined : label}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        setOpen(true);
        setHighlight(-1);
      }}
      onFocus={() => setOpen(true)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setOpen(true);
          setHighlight((h) => Math.min(h + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlight((h) => Math.max(h - 1, -1));
        } else if (e.key === 'Enter' && showList && highlight >= 0 && filtered[highlight]) {
          // Picking a suggestion; a plain Enter submits the surrounding form.
          e.preventDefault();
          commit(filtered[highlight].name);
        } else if (e.key === 'Escape') {
          setOpen(false);
        }
      }}
      placeholder={placeholder}
      autoComplete="off"
      className={cn(
        'w-full min-w-0 bg-transparent font-sans text-sl-ink placeholder:text-sl-label-2 focus:outline-none',
        style === 'segment' ? 'mt-1 text-[15px]' : 'text-[14.5px]',
      )}
    />
  );

  return (
    <div ref={wrapRef} className={cn('relative min-w-0', className)}>
      {style === 'segment' ? (
        <label htmlFor={id} className="flex cursor-text flex-col px-5 py-2.5">
          <span className="text-[10.5px] font-bold uppercase tracking-[1.6px] text-sl-label-2">{label}</span>
          {input}
        </label>
      ) : (
        <label
          htmlFor={id}
          className={cn(
            'flex cursor-text items-center gap-2.5',
            style === 'field'
              ? 'h-ctl-md rounded-12 border border-sl-field-line bg-sl-surface px-3.5 focus-within:border-sl-accent'
              : 'h-[40px] px-3',
          )}
        >
          <MapPin className="h-4 w-4 shrink-0 text-sl-label" strokeWidth={1.8} aria-hidden="true" />
          {input}
        </label>
      )}

      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 min-w-[220px] overflow-y-auto rounded-14 border border-sl-line bg-sl-surface p-1.5 text-left shadow-book-card"
        >
          {filtered.map((c, i) => {
            const active = i === highlight;
            return (
              <li
                key={c.name}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={active}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  // mousedown beats blur: keep focus until we commit.
                  e.preventDefault();
                  commit(c.name);
                }}
                className={cn(
                  'flex cursor-pointer items-center justify-between rounded-10 px-3 py-2.5 text-[14px]',
                  active ? 'bg-sl-accent-tint text-sl-link' : 'text-sl-ink hover:bg-sl-sand',
                )}
              >
                <span className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
                  {c.name}
                </span>
                <span className="text-[12px] tabular-nums text-sl-label-2">{c.salonCount}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});
